import { Dispatch, FC, ReactNode, RefObject, SetStateAction, useRef, useState } from 'react';

import { useAuth } from 'contexts';
import { Role } from 'types';

import { TreeMapData } from './charts/tree-map';

type AuthorizedRoles = { [P in Role]?: boolean };
export interface ChartComponentProps {
  boxRef: RefObject<HTMLDivElement>;
  focused?: boolean;
  filter?: <T extends TreeMapData>(node: T) => boolean;
  setTitleElement?: Dispatch<SetStateAction<ReactNode>>;
}
type ChartCardProps<T extends string> = AuthorizedRoles &
  Omit<ChartComponentProps, 'boxRef' | 'focused'> & {
    title: T;
    Chart: FC<ChartComponentProps>;
    className?: string;
    focused?: T;
    setFocused: Dispatch<SetStateAction<T | undefined>>;
  };

const ChartCard = <T extends string>({
  title,
  Chart,
  className = '',
  setFocused,
  filter,
  focused,
  setTitleElement: _,
  ...roles
}: ChartCardProps<T>) => {
  const { userProfile } = useAuth();

  const [titleElement, setTitleElement] = useState<ReactNode>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  return !roles[userProfile.role] || (focused && focused !== title) ? null : (
    <div
      className={`chart-card panel h-100 overflow-hidden ${focused ? undefined : className}`}
      id={`${title.replace(/\s/g, '-')}-card`}
    >
      <div className="d-flex justify-content-between">
        <h4 className="font-weight-medium">{title}</h4>
        {titleElement}
        {!!focused ? (
          <button className="btn-back p-2 expand" onClick={() => setFocused(undefined)}>
            <span className="material-icons icon-large">close_fullscreen</span>
          </button>
        ) : (
          <button className="btn-back p-2 expand" onClick={() => setFocused(title)}>
            <span className="material-icons icon-large">open_in_full</span>
          </button>
        )}
      </div>
      <div className="w-100 flex-fill overflow-hidden" ref={boxRef}>
        <Chart
          key={title}
          boxRef={boxRef}
          filter={filter}
          focused={focused === title}
          setTitleElement={setTitleElement}
        />
      </div>
    </div>
  );
};

export default ChartCard;
